import React from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import Spinner from '../common/Spinner';

const ActivityFeed = ({ activities = null, loading = false }) => {
  // Format activities from API
  const formatActivities = (apiActivities) => {
    if (!apiActivities || !Array.isArray(apiActivities) || apiActivities.length === 0) {
      console.log('No activities to format:', apiActivities);
      return [];
    }
    
    console.log('Formatting activities:', apiActivities);
    
    return apiActivities.map((activity, index) => {
      // Get icon based on type
      let icon;
      const activityType = activity.type || activity.activity_type || 'info';
      switch (activityType) {
        case 'success':
          icon = <FaCheckCircle />;
          break;
        case 'warning':
          icon = <FaExclamationTriangle />;
          break;
        case 'error':
          icon = <FaExclamationTriangle />;
          break;
        case 'info':
          icon = <FaInfoCircle />;
          break;
        default:
          icon = <FaInfoCircle />;
      }
      
      // Format time - check both 'time' and 'activity_time' fields
      let timeText = '';
      const timeValue = activity.time || activity.activity_time || activity.timestamp;
      if (timeValue) {
        try {
          const activityTime = new Date(timeValue);
          if (!isNaN(activityTime.getTime())) {
            const now = new Date();
            const diffMs = now - activityTime;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            
            if (diffMins < 1) {
              timeText = 'Just now';
            } else if (diffMins < 60) {
              timeText = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
            } else if (diffHours < 24) {
              timeText = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
            } else if (diffDays < 7) {
              timeText = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
            } else {
              timeText = activityTime.toLocaleDateString();
            }
          }
        } catch (e) {
          console.error('Error formatting time:', e, timeValue);
        }
      }
      
      // Extract strategy name from activity data
      let strategyName = '';
      
      // Debug: Log the activity to see what we're working with
      console.log('Processing activity:', {
        id: activity.id,
        title: activity.title,
        description: activity.description,
        strategy_name: activity.strategy_name,
        source: activity.source
      });
      
      // First check if strategy_name is directly available
      if (activity.strategy_name && activity.strategy_name.trim() !== '') {
        strategyName = activity.strategy_name.trim();
        console.log('Found strategy_name from field:', strategyName);
      } else if (activity.title) {
        // Try to extract from title - multiple patterns
        // Pattern 1: Strategy "Name" or Strategy 'Name'
        let titleMatch = activity.title.match(/Strategy\s+"([^"]+)"/i) || 
                        activity.title.match(/Strategy\s+'([^']+)'/i);
        
        // Pattern 2: "Name" executed or 'Name' executed
        if (!titleMatch) {
          titleMatch = activity.title.match(/"([^"]+)"\s+(?:executed|created|updated)/i) ||
                      activity.title.match(/'([^']+)'\s+(?:executed|created|updated)/i);
        }
        
        // Pattern 3: Just look for quoted text after "Strategy"
        if (!titleMatch) {
          titleMatch = activity.title.match(/Strategy[^"']*"([^"]+)"/i) ||
                      activity.title.match(/Strategy[^"']*'([^']+)'/i);
        }
        
        if (titleMatch && titleMatch[1]) {
          strategyName = titleMatch[1].trim();
          console.log('Extracted strategy_name from title:', strategyName);
        }
      }
      
      // Fallback: Try description
      if (!strategyName && activity.description) {
        const descMatch = activity.description.match(/Strategy\s+"([^"]+)"/i) || 
                         activity.description.match(/Strategy\s+'([^']+)'/i) ||
                         activity.description.match(/"([^"]+)"\s+(?:executed|created|updated)/i) ||
                         activity.description.match(/'([^']+)'\s+(?:executed|created|updated)/i);
        if (descMatch && descMatch[1]) {
          strategyName = descMatch[1].trim();
          console.log('Extracted strategy_name from description:', strategyName);
        }
      }
      
      console.log('Final strategyName:', strategyName || 'NOT FOUND');
      
      return {
        id: activity.id || index,
        type: activityType,
        title: activity.title || '',
        description: activity.description || '',
        time: timeText,
        icon: icon,
        strategyName: strategyName
      };
    });
  };

  // Debug: Log activities prop
  React.useEffect(() => {
    console.log('ActivityFeed received activities:', activities);
    console.log('Activities type:', typeof activities);
    console.log('Is array:', Array.isArray(activities));
    console.log('Length:', activities?.length);
  }, [activities]);

  // Handle activities - check if it's an array and has items
  // If activities is null/undefined, it means it hasn't been loaded yet - show loading state
  // If activities is an empty array, it means no activities found - show empty state
  // If activities has items, format and show them
  const hasActivities = activities !== null && Array.isArray(activities) && activities.length > 0;
  const formattedActivities = hasActivities ? formatActivities(activities) : [];
  
  // Never show default/mock activities - always show real data or empty state
  const activitiesToShow = formattedActivities;
  
  // Determine if we're still loading
  const isLoading = loading || activities === null;
  
  console.log('ActivityFeed - activities prop:', activities);
  console.log('ActivityFeed - hasActivities:', hasActivities);
  console.log('ActivityFeed - formattedActivities:', formattedActivities);
  console.log('ActivityFeed - isLoading:', isLoading);

  const getActivityColor = (type) => {
    switch (type) {
      case 'success':
        return '#28a745';
      case 'warning':
        return '#ffc107';
      case 'error':
        return '#dc3545';
      case 'info':
        return '#17a2b8';
      default:
        return '#6c757d';
    }
  };

  const getActivityBgColor = (type) => {
    switch (type) {
      case 'success':
        return '#d4edda';
      case 'warning':
        return '#fff3cd';
      case 'error':
        return '#f8d7da';
      case 'info':
        return '#d1ecf1';
      default:
        return '#f8f9fa';
    }
  };

  return (
    <div className="activity-feed" style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      borderRadius: '10px',
      padding: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e9ecef',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <div className="d-flex align-items-center mb-2">
        <h4 className="mb-0 fw-bold" style={{ color: '#151B22', fontSize: '16px' }}>
          Recent Activity
        </h4>
        <div style={{
          width: '30px',
          height: '2px',
          background: 'linear-gradient(90deg, #4DD5B8 0%, #FF823C 100%)',
          borderRadius: '2px',
          marginLeft: '8px'
        }} />
      </div>

      <div className="activity-list" style={{
        flex: 1,
        overflowY: 'auto',
        paddingRight: '8px',
        minHeight: 0
      }}>
        {isLoading ? (
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 10px',
            minHeight: '150px'
          }}>
            <Spinner size="sm" text="Loading..." />
          </div>
        ) : activitiesToShow.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px 10px',
            color: '#6c757d'
          }}>
            <FaInfoCircle style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontSize: '12px', fontWeight: '500' }}>No recent activities</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#495057' }}>
              Your recent notifications and strategy executions will appear here
            </p>
          </div>
        ) : (
          activitiesToShow.map((activity, index) => (
          <div 
            key={activity.id || index}
            className="activity-item"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '10px',
              marginBottom: '8px',
              background: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              transition: 'all 0.3s ease',
              position: 'relative'
            }}
          >
            {/* Activity indicator line */}
            <div style={{
              position: 'absolute',
              left: '0',
              top: '0',
              bottom: '0',
              width: '3px',
              background: getActivityColor(activity.type),
              borderTopLeftRadius: '8px',
              borderBottomLeftRadius: '8px'
            }} />

            {/* Icon */}
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: getActivityBgColor(activity.type),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: getActivityColor(activity.type),
              fontSize: '14px',
              marginRight: '10px',
              flexShrink: 0
            }}>
              {activity.icon}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {activity.strategyName && activity.strategyName.trim() !== '' && (
                <div style={{
                  display: 'inline-block',
                  padding: '2px 6px',
                  background: 'linear-gradient(135deg, #053A62 0%, #4DD5B8 100%)',
                  color: '#fff',
                  borderRadius: '4px',
                  fontSize: '9px',
                  fontWeight: '600',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {activity.strategyName}
                </div>
              )}
              
              <h6 className="mb-0 fw-semibold" style={{ 
                color: '#151B22', 
                fontSize: '12px',
                margin: (activity.strategyName && activity.strategyName.trim() !== '') ? '4px 0 0 0' : 0,
                lineHeight: '1.3'
              }}>
                {activity.title}
              </h6>
              
              <p className="mb-1 mt-1" style={{ 
                color: '#495057', 
                fontSize: '11px',
                margin: 0,
                lineHeight: '1.3'
              }}>
                {activity.description}
              </p>
              
              <span style={{ 
                fontSize: '10px', 
                color: '#6c757d',
                fontWeight: '500'
              }}>
                {activity.time}
              </span>
            </div>
          </div>
        ))
        )}
      </div>

      <div className="activity-footer text-center mt-2">
        <button 
          className="btn btn-outline-primary btn-sm"
          style={{
            fontSize: '11px',
            padding: '4px 12px',
            borderRadius: '6px',
            border: '1px solid #4DD5B8',
            color: '#4DD5B8',
            background: 'transparent',
            transition: 'all 0.3s ease'
          }}
        >
          View All Activities
        </button>
      </div>

      <style jsx>{`
        .activity-item:hover {
          transform: translateX(4px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          border-color: #4DD5B840;
        }
        
        .activity-list::-webkit-scrollbar {
          width: 6px;
        }
        
        .activity-list::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        .activity-list::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        
        .activity-list::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default ActivityFeed;
